import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';

import {
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  onSnapshot,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Date formatter for DD MMM YYYY
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Date-time formatter for header
const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Date formatter for filename
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Natural sort for batch codes
const naturalSort = (a, b) => {
  const codeA = String(a.batchCode ?? '').trim();
  const codeB = String(b.batchCode ?? '').trim();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const Batches = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newBatch, setNewBatch] = useState({ 
    name: '',
    batchCode: '',
    description: '',
    active: true
  });
  const [editingBatch, setEditingBatch] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [isFetchingBatches, setIsFetchingBatches] = useState(true);
  const [hasFetchedBatches, setHasFetchedBatches] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const batchesCollectionRef = collection(db, "batches");
  const hotelName = `${HOTEL_NAME}`;

  useEffect(() => {
    if (!user?.uid || !userData) return;

    setIsFetchingBatches(true);

    const q = query(batchesCollectionRef);

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const batchList = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id
        }));
        setBatches(batchList);
        setHasFetchedBatches(true);
        setIsFetchingBatches(false);
      },
      (err) => {
        console.error("Error in batches listener:", err);
        setError("Failed to load batches");
        setShowModal(true);
        setIsFetchingBatches(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, userData]);

  useEffect(() => {
    const filtered = batches.filter((batch) =>
      (batch.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (batch.batchCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (batch.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBatches(filtered);
  }, [searchTerm, batches]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBatch({ ...newBatch, [name]: value });
  };

  const handleAddBatch = async () => {
    if (!newBatch.name.trim()) {
      setError("Batch name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(batchesCollectionRef, {
        ...newBatch,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Batch added successfully");
      setNewBatch({ 
        name: '',
        batchCode: '',
        description: '',
        active: true
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add batch");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateBatch = async () => {
    setUpdate(true);
    try {
      const batchDoc = doc(db, "batches", editingBatch.id);
      await updateDoc(batchDoc, {
        ...newBatch,
        updatedAt: Timestamp.now()
      });
      setSuccessMessage("Batch updated successfully");
      setEditingBatch(null);
      setNewBatch({ 
        name: '',
        batchCode: '',
        description: '',
        active: true
      });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update batch");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  
  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setError("");
    setSuccessMessage("");

    try {
      let batchesData = [];
      let headers = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          blankrows: false
        });

        if (json.length < 1) throw new Error("File is empty");

        headers = json[0].map(h => (h || '').toString().trim().toLowerCase());
        batchesData = json.slice(1);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.trim().toLowerCase()
        });

        if (result.errors.length > 0) {
          throw new Error("CSV parsing error: " + result.errors[0].message);
        }

        headers = Object.keys(result.data[0] || {}).map(h => h.toLowerCase());
        batchesData = result.data;
      }

      if (batchesData.length === 0) {
        throw new Error("No data rows found after header");
      }

      const colMap = {
        name: headers.find(h => ['name', 'batchname', 'title'].includes(h)),
        batchcode: headers.find(h => ['batchcode', 'batchnumber', 'code', 'id'].includes(h)),
        description: headers.find(h => ['description', 'desc', 'note'].includes(h)),
        active: headers.find(h => ['active', 'status'].includes(h))
      };

      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < batchesData.length; i++) {
        const row = batchesData[i];
        
        const getValue = (key) => {
          if (fileType === 'excel') {
            const idx = headers.indexOf(colMap[key]);
            return idx !== -1 ? row[idx] : undefined;
          } else {
            return row[colMap[key]];
          }
        };

        const rawName = getValue('name');
        const nameStr = (rawName ?? '').toString().trim();

        if (!nameStr) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing batch name`);
          continue;
        }

        const batchData = {
          name: nameStr,
          batchCode: (getValue('batchcode') ?? '').toString().trim(),
          description: (getValue('description') ?? '').toString().trim(),
          active: getValue('active') !== undefined 
            ? String(getValue('active')).toLowerCase() === 'true' || 
              String(getValue('active')).toLowerCase() === 'yes' || 
              getValue('active') === 1 || getValue('active') === true
            : true,
          createdAt: Timestamp.now(),
          userId: user?.uid || ''
        };

        const newDocRef = doc(batchesCollectionRef);
        batch.set(newDocRef, batchData);
        successCount++;
      }

      if (successCount > 0) {
        await batch.commit();
      }

      let message = `Imported ${successCount} batch${successCount === 1 ? '' : 'es'} successfully`;
      if (errorCount > 0) {
        message += ` • ${errorCount} row${errorCount === 1 ? '' : 's'} skipped`;
      }
      setSuccessMessage(message);

      if (errors.length > 0) {
        console.warn("Import issues:\n" + errors.join("\n"));
      }

      setFile(null);
      setIsBulkImportModalOpen(false);

    } catch (error) {
      console.error("Bulk import failed:", error);
      setError(`Bulk import failed: ${error.message || "Unknown error"}`);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const generateBatchesPDF = () => {
    if (batches.length === 0) {
      setError("No batches available to generate PDF");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let currentY = 10;
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, currentY + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Batches Report", 35, currentY + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    currentY += 28;

    const sortedBatches = [...batches].sort(naturalSort);

    const tableData = sortedBatches.map((batch, index) => [
      (index + 1).toString(),
      batch.batchCode || '-',
      batch.name,
      batch.description || '-',
      batch.active ? 'Active' : 'Inactive',
      batch.createdAt ? dateFormatter.format(batch.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Batch Code', 'Batch Name', 'Description', 'Status', 'Created Date']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 14, right: 10 },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 40 },
        3: { cellWidth: 60 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25 }
      }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`batches_report_${formatDateForFilename(new Date())}.pdf`);
  };

  const handleEditBatch = (batchToEdit) => {
    setEditingBatch(batchToEdit);
    setNewBatch({ 
      name: batchToEdit.name || '',
      batchCode: batchToEdit.batchCode || '',
      description: batchToEdit.description || '',
      active: batchToEdit.active !== undefined ? batchToEdit.active : true
    });
    setIsEditModalOpen(true);
  };

  const confirmDeactivateBatch = (batchToDeactivate) => {
    setCurrentBatch(batchToDeactivate);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateBatch = async (id) => {
    setUpdate(true);
    try {
      const batchDoc = doc(db, "batches", id);
      await updateDoc(batchDoc, { active: false });
      setSuccessMessage("Batch deactivated successfully");
    } catch (error) {
      setError("Failed to deactivate batch");
    } finally {
      setUpdate(false);
      setIsDeactivateModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme', 'Manager'];

  const displayContent = () => {
    if (isFetchingBatches && !hasFetchedBatches) {
      return <p className="loading-text">Loading batches ...</p>;
    }
    
    if (filteredBatches.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No batches match your search.</p>;
      } else {
        return <p className="loading-text">No batches found. Add your first batch or import in bulk.</p>;
      }
    }
    
    return filteredBatches.map((batchItem) => (
      <div key={batchItem.id} className="class-item">
        <div className="column gap-1">
          <p>
            <strong>{batchItem.name}</strong><br />
            <span>Code: {batchItem.batchCode || 'N/A'}</span><br />
            <span>{batchItem.description || 'No description provided'}</span><br />
            <span className="size5 font-gold bold">{batchItem.active ? 'Active' : 'Not active'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditBatch(batchItem)}>
            <i className="fas fa-edit"></i>
          </button>
          {batchItem.active && (
            <button className="list-btn delete-btn" onClick={() => confirmDeactivateBatch(batchItem)}>
              <i className="fas fa-ban"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Batches</h2>
            <Link to="#" className="home-link" onClick={() => window.location.reload()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
            <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Batch</button>    
              <button className="list-btn" onClick={generateBatchesPDF}><i className="fas fa-print"></i></button>          
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>                            
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name, code or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* ====================== ADD BATCH MODAL ====================== */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Batch"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add New Batch</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Batch Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Batch Name"
            value={newBatch.name}
            onChange={handleInputChange}
            required
          />

          <label htmlFor="batchCode" className="input-label">Batch Code</label>
          <input
            type="text"
            name="batchCode"
            className="input-field"
            placeholder="e.g. BATCH-2025-001"
            value={newBatch.batchCode}
            onChange={handleInputChange}
          />

          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            placeholder="Batch description (optional)"
            value={newBatch.description}
            onChange={handleInputChange}
            rows="3"
          />

          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddBatch}>Add Batch</button>
          </div>
        </form>
      </Modal>

      {/* ====================== EDIT BATCH MODAL ====================== */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewBatch({ name: '', batchCode: '', description: '', active: true });
          setEditingBatch(null);
        }}
        contentLabel="Edit Batch"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Batch</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Batch Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            value={newBatch.name}
            onChange={handleInputChange}
            required
          />

          <label htmlFor="batchCode" className="input-label">Batch Code</label>
          <input
            type="text"
            name="batchCode"
            className="input-field"
            value={newBatch.batchCode}
            onChange={handleInputChange}
          />

          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            value={newBatch.description}
            onChange={handleInputChange}
            rows="3"
          />

          <label>
            <input
              type="checkbox"
              checked={newBatch.active}
              onChange={(e) => setNewBatch({ ...newBatch, active: e.target.checked })}
            />
            Active
          </label>

          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              setNewBatch({ name: '', batchCode: '', description: '', active: true });
              setEditingBatch(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateBatch}>Update Batch</button>
          </div>
        </form>
      </Modal>

      {/* ====================== DEACTIVATE MODAL ====================== */}
      <Modal
        isOpen={isDeactivateModalOpen}
        onRequestClose={() => setIsDeactivateModalOpen(false)}
        contentLabel="Deactivate Batch"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Deactivate Batch</h2>
        {currentBatch && (
          <div>
            <p>Are you sure you want to deactivate <strong>{currentBatch.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeactivateBatch(currentBatch.id)}>Deactivate</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ====================== BULK IMPORT MODAL ====================== */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
        }}
        contentLabel="Bulk Import Batches"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Batches</h2>
        <div className="modal-form">
          <div style={{marginBottom: '10px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label style={{marginLeft: '15px'}}>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name</p>
            <p><strong>OPTIONAL:</strong> batchCode, description</p>
            <p><strong>FOR EXCEL/CSV:</strong> First row must contain column headers</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Batches;